import { defineStore } from 'pinia';
import type { UserPayload } from '@shared/api';
import { useSocialStore } from '@features/social/store/social';
import { authService, transactionalService } from '@shared/api';
import { toast } from '@shared/utils/toast';
import { type Router } from 'vue-router';
import { handleAxiosError } from '@shared/utils/error';
import { isBlockedUserError } from '@shared/utils/setupUserBlockInterceptor';
import { queryClient, resetSessionQueries } from '@infra/plugins/vue-query.ts';
import { type AxiosError } from 'axios';
import { useTenantStore } from '@features/tenant/store/tenant';
import {
  currentPlayerFrom,
  type CurrentPlayer,
} from '@features/auth/current-player';

interface AuthState {
  user: UserPayload | null;
  currentPlayer: CurrentPlayer | null;
  sessionRevision: number;
  loginError: string;
  showLoginModal: boolean;
  redirectToHomeOnCloseLoginModal: boolean;
  canDoModalProps: CanDoModalProps;
  redirectToHomeOnCloseCanDoModal: boolean;
  showComingSoonModal: boolean;
  showWelcomeModal: boolean;
  showAccountBlockedModal: boolean;
  accountBlockedReason: string;
}

export interface RegistrationData {
  email: string;
  password: string;
  confirmPassword: string;
  username: string;
  tokenCode: string;
}

export interface CanDoModalProps {
  showCanDoModal: boolean;
  canDoTitle: string;
  canDoMessage: string;
}

export const sso = {
  Facebook: 'facebook',
  Google: 'google',
  Microsoft: 'microsoft',
  UPM: 'upm',
  Apple: 'apple',
} as const;

export type Sso = (typeof sso)[keyof typeof sso];

const updateSocialUserImage = async (picture: string) => {
  await transactionalService.updateSocialUserImage(picture);
  await useSocialStore().getMyUser();
  await queryClient.refetchQueries({
    queryKey: ['social-users'],
  });
};

let _pendingSsoCode: string | null = null;

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null as UserPayload | null,
    currentPlayer: null,
    sessionRevision: 0,
    showLoginModal: false,
    redirectToHomeOnCloseLoginModal: false,
    redirectToHomeOnCloseCanDoModal: false,
    loginError: '',
    canDoModalProps: {
      showCanDoModal: false,
      canDoTitle: '',
      canDoMessage: '',
    },
    showComingSoonModal: false,
    showWelcomeModal: false,
    showAccountBlockedModal: false,
    accountBlockedReason: '',
  }),
  actions: {
    async clearLocalSession() {
      this.sessionRevision += 1;
      this.user = null;
      this.currentPlayer = null;
      useSocialStore().clearMyUser();
      await resetSessionQueries();
    },

    async hydrateCurrentPlayer() {
      const revision = this.sessionRevision;
      const tenant = useTenantStore();
      const tenantVersion = tenant.resolveVersion;
      const operationId = tenant.operationId;
      try {
        const { data } = await authService.getCurrentUser();
        if (
          revision !== this.sessionRevision ||
          tenantVersion !== tenant.resolveVersion ||
          operationId !== tenant.operationId
        ) {
          return null;
        }
        const player = currentPlayerFrom(data, tenant);
        if (!player) {
          await this.clearLocalSession();
          return null;
        }
        this.currentPlayer = player;
        await useSocialStore().getMyUser();
        if (
          revision !== this.sessionRevision ||
          tenantVersion !== tenant.resolveVersion ||
          operationId !== tenant.operationId ||
          this.currentPlayer.playerId !== player.playerId
        ) {
          return null;
        }
        this.currentPlayer = currentPlayerFrom(
          data,
          tenant,
          useSocialStore()._socialUser,
        );
        return this.currentPlayer;
      } catch {
        if (revision === this.sessionRevision) await this.clearLocalSession();
        return null;
      }
    },
    async login(
      email: string,
      password: string,
      isSso?: Sso,
      ssoAuthCode?: string,
      firstName?: string,
      lastName?: string,
    ) {
      // Deduplicate concurrent SSO code exchanges — if the same code is already
      // being exchanged (race between guard + popup poller), skip the second call.
      if (ssoAuthCode && isSso) {
        if (_pendingSsoCode === ssoAuthCode) return;
        _pendingSsoCode = ssoAuthCode;
      }
      const revision = ++this.sessionRevision;
      try {
        const resApi = await authService.login({
          email,
          password,
          isSso: isSso ?? '',
          ssoAuthCode: ssoAuthCode ?? '',
          firstName,
          lastName,
        });

        if (revision !== this.sessionRevision) return;
        if (this.user?.id !== resApi.data.id) {
          useSocialStore().clearMyUser();
          await resetSessionQueries();
          if (revision !== this.sessionRevision) return;
        }
        this.user = resApi.data;
        if (!(await this.hydrateCurrentPlayer())) return;

        switch (resApi.status) {
          case 200:
            //load op in background, the user is now logged in
            void useSocialStore().getMyUser();
            break;
          case 201:
            {
              // new user, needs to be awaited
              const picture = (
                resApi.data as UserPayload & { picture?: string }
              ).picture;
              if (!!picture && picture !== '') {
                // async operations that don't need to be awaited
                void updateSocialUserImage(picture);
              } else {
                //load op in background, the user is now logged in
                void useSocialStore().getMyUser();
              }
            }
            break;
          default:
            throw new Error('Código de estado inesperado');
        }

        toast.success({
          title: 'Inicio de sesión exitoso',
          message: `Ahora has iniciado sesión como ${this.user.username}`,
        });
      } catch (error) {
        // Blocked-user 403s are already handled by the user-block
        // interceptor (modal or toast) — avoid a second toast here.
        if (!isBlockedUserError(error)) {
          handleAxiosError(error, true);
        }
      } finally {
        _pendingSsoCode = null;
      }
    },

    async validateCookie() {
      const socialStore = useSocialStore();
      const revision = this.sessionRevision;
      try {
        const res = await authService.refreshCookie();

        if (revision !== this.sessionRevision) return false;
        const refreshed = res.data;
        if (this.user?.id !== refreshed.id) {
          socialStore.clearMyUser();
          await resetSessionQueries();
          if (revision !== this.sessionRevision) return false;
        }
        this.user = this.user ? { ...this.user, ...refreshed } : refreshed;
        return Boolean(await this.hydrateCurrentPlayer());
      } catch (error) {
        socialStore.isLoadingUser = false;
        return false;
      }
    },
    async logout() {
      const hadUser = this.user !== null;
      this.sessionRevision += 1;
      this.user = null;
      this.currentPlayer = null;
      useSocialStore().clearMyUser();
      const cacheCleanup = resetSessionQueries().catch((error: unknown) => {
        console.error(error);
      });
      const remoteLogout = async () => {
        try {
          if (hadUser) await authService.logout();
        } catch (error) {
          console.error(error);
        }
      };
      await Promise.allSettled([cacheCleanup, remoteLogout()]);
      if (hadUser) {
        await (this.router as Router).push({ path: '/' });
        toast.info({
          title: 'Ahora has cerrado sesión.',
          message:
            'Inicia sesión nuevamente para desbloquear todo nuestro contenido.',
        });
      }
    },
    async refreshCookie() {
      const revision = this.sessionRevision;
      try {
        const res = await authService.refreshCookie();
        if (revision !== this.sessionRevision) return;
        if (this.user?.id !== res.data.id) {
          useSocialStore().clearMyUser();
          await resetSessionQueries();
          if (revision !== this.sessionRevision) return;
        }
        this.user = res.data;
        if (!(await this.hydrateCurrentPlayer())) return;
        return this.user;
      } catch (error) {
        if ((error as AxiosError).status === 409) {
          return 'conflict-another-device';
        }
        console.error(error);
      }
    },
    async getAuthUrl() {
      const { data } = await authService.getAuthUrl();
      return data;
    },
    async connectToRoom(room: string) {
      try {
        await authService.connectToRoom(room);
        return true;
      } catch (error) {
        if ((error as AxiosError).status === 400) {
          toast.error({
            title: 'Error',
            message: 'Ya estás conectado a esta sala.',
          });
        }
        return false;
      }
    },
    async refreshConnection(room: string) {
      try {
        await authService.refreshConnection(room);
      } catch (error) {
        console.error((error as AxiosError).response);
      }
    },
  },
});
